import React, { useState, useEffect } from 'react';
import { getSubTeamLeads } from '../../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function SubTeamDashboard({ user, selectedSubTeam, onBackToTeam }) {
  const [subTeamStats, setSubTeamStats] = useState({
    totalLeads: 0,
    qualified: 0,
    noInterest: 0,
    revoked: 0,
    notQualified: 0,
    loading: true,
    error: null
  });

  const [chartData, setChartData] = useState({
    topPerformers: [],
    dailyLeads: [],
    loading: true
  });

  // Sub-Team-Statistiken laden
  useEffect(() => {
    const loadSubTeamStatistics = async () => {
      if (!user?.id || !selectedSubTeam?.id) {
        setSubTeamStats(prev => ({ ...prev, loading: false }));
        setChartData(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        setSubTeamStats(prev => ({ ...prev, loading: true, error: null }));
        setChartData(prev => ({ ...prev, loading: true }));
        
        const response = await getSubTeamLeads(user.id, selectedSubTeam.id);
        
        if (response.success) {
          const leads = response.leads || [];
          
          // Datum vor 2 Wochen berechnen
          const twoWeeksAgo = new Date();
          twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
          
          // Leads der letzten 2 Wochen filtern
          const recentLeads = leads.filter(lead => {
            const leadDate = new Date(lead.erstellt);
            return leadDate >= twoWeeksAgo;
          });
          
          // Statistiken berechnen
          const stats = {
            totalLeads: recentLeads.length,
            qualified: 0,
            noInterest: 0,
            revoked: 0,
            notQualified: 0,
            loading: false,
            error: null
          };
          
          recentLeads.forEach(lead => {
            // PV-Status prüfen
            if (lead.status_pv) {
              const pvStatus = lead.status_pv.toLowerCase();
              if (pvStatus.includes('qualifiziert')) {
                stats.qualified++;
              } else if (pvStatus.includes('kein interesse')) {
                stats.noInterest++;
              } else if (pvStatus.includes('widerruf')) {
                stats.revoked++;
              } else if (pvStatus.includes('test lead') || pvStatus.includes('telefonnummer ungültig') || lead.status_pv_invalid) {
                stats.notQualified++;
              }
            }
            
            // WP-Status prüfen
            if (lead.status_wp) {
              const wpStatus = lead.status_wp.toLowerCase();
              if (wpStatus.includes('qualifiziert')) {
                stats.qualified++;
              } else if (wpStatus.includes('kein interesse')) {
                stats.noInterest++;
              } else if (wpStatus.includes('widerruf')) {
                stats.revoked++;
              } else if (wpStatus.includes('test lead') || wpStatus.includes('telefonnummer ungültig') || lead.status_wp_invalid) {
                stats.notQualified++;
              }
            }
          });
          
          setSubTeamStats(stats);

          // Chart-Daten verarbeiten
          processChartData(leads);
          
        } else {
          setSubTeamStats(prev => ({
            ...prev,
            loading: false,
            error: response.error || 'Fehler beim Laden der Sub-Team-Statistiken'
          }));
          setChartData(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        setSubTeamStats(prev => ({
          ...prev,
          loading: false,
          error: 'Netzwerkfehler: ' + err.message
        }));
        setChartData(prev => ({ ...prev, loading: false }));
      }
    };

    const processChartData = (allLeads) => {
      // Top Performer Daten generieren
      const userLeadsMap = {};
      
      allLeads.forEach(lead => {
        // Versuche verschiedene Felder für den Benutzernamen
        const userName = lead.user_name || lead.dienstleister || 'Unbekannt';
        
        if (!userLeadsMap[userName]) {
          userLeadsMap[userName] = { total: 0, qualified: 0 };
        }
        userLeadsMap[userName].total++;
        
        // Qualifizierte zählen
        let isQualified = false;
        if (lead.status_pv && lead.status_pv.toLowerCase().includes('qualifiziert')) {
          isQualified = true;
        }
        if (lead.status_wp && lead.status_wp.toLowerCase().includes('qualifiziert')) {
          isQualified = true;
        }
        
        if (isQualified) {
          userLeadsMap[userName].qualified++;
        }
      });

      // Top 3 Performer sortieren
      const topPerformers = Object.entries(userLeadsMap)
        .map(([name, data]) => ({
          name: name.length > 12 ? name.substring(0, 12) + '...' : name,
          fullName: name,
          Gesamt: data.total,
          Qualifiziert: data.qualified
        }))
        .sort((a, b) => b.Gesamt - a.Gesamt)
        .slice(0, 3);

      // Daily Leads für letzte 60 Tage
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      
      const dailyLeadsMap = {};
      
      // Alle Tage der letzten 60 Tage initialisieren
      for (let i = 59; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit'
        });
        dailyLeadsMap[dateStr] = 0;
      }
      
      // Leads zu den entsprechenden Tagen hinzufügen
      allLeads.forEach(lead => {
        const leadDate = new Date(lead.erstellt);
        if (leadDate >= sixtyDaysAgo) {
          const dateStr = leadDate.toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit'
          });
          if (dailyLeadsMap[dateStr] !== undefined) {
            dailyLeadsMap[dateStr]++;
          }
        }
      });

      const dailyLeads = Object.entries(dailyLeadsMap).map(([date, count]) => ({
        date,
        leads: count
      }));

      setChartData({
        topPerformers,
        dailyLeads,
        loading: false
      });
    };

    loadSubTeamStatistics();
  }, [user?.id, selectedSubTeam?.id]);

  const TopPerformerTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#2d3748' }}>
            {data.fullName}
          </p>
          <p style={{ margin: '0 0 4px 0', fontSize: '0.875rem', color: '#4a5568' }}>
            Gesamt: {data.Gesamt}
          </p>
          <p style={{ margin: '0', fontSize: '0.875rem', color: '#4a5568' }}>
            Qualifiziert: {data.Qualifiziert}
          </p>
        </div>
      );
    }
    return null;
  };

  const DailyLeadsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#2d3748' }}>
            {label}
          </p>
          <p style={{ margin: '0', fontSize: '0.875rem', color: '#4a5568' }}>
            Leads: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  const getPercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <div className="team-dashboard-container">
      <div className="team-dashboard-header">
        {/* Back Button */}
        <button 
          onClick={onBackToTeam}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '6px 12px',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#374151',
            marginBottom: '16px',
            transition: 'all 0.2s ease',
            fontWeight: '500',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#9ca3af';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.borderColor = '#d1d5db';
            e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5"/>
            <path d="M12 19l-7-7 7-7"/>
          </svg>
          Zurück zum Team
        </button>
        
        <h2>Sub-Team Dashboard - {selectedSubTeam?.name}</h2>
        <span className="team-name">{selectedSubTeam?.name}</span>
      </div>
      
      {subTeamStats.loading ? (
        <div className="stats-loading">
          <div className="loading-spinner"></div>
          <span>Lade Sub-Team-Statistiken...</span>
        </div>
      ) : subTeamStats.error ? (
        <div className="stats-error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>{subTeamStats.error}</span>
        </div>
      ) : (
        <>
          <div className="team-stats-grid">
            {/* Gesamte Leads */}
            <div className="stat-card total-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{subTeamStats.totalLeads}</div>
                <div className="stat-label">Leads eingegangen</div>
              </div>
            </div>
            
            {/* Qualifizierte Leads */}
            <div className="stat-card qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{subTeamStats.qualified}</div>
                <div className="stat-label">Qualifiziert</div>
                <div className="stat-percentage">
                  {getPercentage(subTeamStats.qualified, subTeamStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Kein Interesse */}
            <div className="stat-card no-interest-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{subTeamStats.noInterest}</div>
                <div className="stat-label">Kein Interesse</div>
                <div className="stat-percentage">
                  {getPercentage(subTeamStats.noInterest, subTeamStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Widerrufen */}
            <div className="stat-card revoked-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18l-2 13H5L3 6z"></path>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{subTeamStats.revoked}</div>
                <div className="stat-label">Widerrufen</div>
                <div className="stat-percentage">
                  {getPercentage(subTeamStats.revoked, subTeamStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Nicht qualifiziert */}
            <div className="stat-card not-qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{subTeamStats.notQualified}</div>
                <div className="stat-label">Nicht qualifiziert</div>
                <div className="stat-percentage">
                  {getPercentage(subTeamStats.notQualified, subTeamStats.totalLeads)}%
                </div>
              </div>
            </div>
          </div>

          {/* Chart-Sektion */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr',
            gap: '24px', 
            marginTop: '32px'
          }}>
            {/* Top Performer Chart */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#2d3748' 
              }}>
                Mitarbeiter Performance
              </h3>
              {chartData.loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Chart-Daten...</span>
                </div>
              ) : chartData.topPerformers.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={chartData.topPerformers} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                      interval={0}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                    />
                    <Tooltip content={<TopPerformerTooltip />} />
                    <Bar 
                      dataKey="Gesamt" 
                      fill="#4a6de5" 
                      radius={[4, 4, 0, 0]}
                      name="Gesamt"
                    />
                    <Bar 
                      dataKey="Qualifiziert" 
                      fill="#48bb78" 
                      radius={[4, 4, 0, 0]}
                      name="Qualifiziert"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096' 
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  <p style={{ margin: '16px 0 0 0', fontSize: '0.875rem' }}>
                    Keine Performance-Daten verfügbar
                  </p>
                </div>
              )}
            </div>

            {/* Daily Leads Chart */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#2d3748' 
              }}>
                Leads der letzten 60 Tage
              </h3>
              {chartData.loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Chart-Daten...</span>
                </div>
              ) : chartData.dailyLeads.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart 
                    data={chartData.dailyLeads} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#4a5568' }}
                      interval={6}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                    />
                    <Tooltip content={<DailyLeadsTooltip />} />
                    <Bar 
                      dataKey="leads" 
                      fill="#4a6de5" 
                      radius={[4, 4, 0, 0]}
                      name="Leads"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096' 
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  <p style={{ margin: '16px 0 0 0', fontSize: '0.875rem' }}>
                    Keine Lead-Daten verfügbar
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SubTeamDashboard; 